import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { TrendingUp, TrendingDown, DollarSign, ShoppingCart, Users, Percent, Package, CreditCard, Truck, Share2, Tag, Star } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';

const COLORS = ['#2D6CDF', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#EC4899', '#84CC16'];
const fmt = (n) => new Intl.NumberFormat('id-ID').format(n);

function StatCard({ icon: Icon, label, value, change, prefix = '', color = 'blue' }) {
  const isUp = change > 0;
  const colorMap = { blue: 'text-[#2D6CDF]', green: 'text-emerald-500', amber: 'text-amber-500', purple: 'text-purple-500', rose: 'text-rose-500' };
  return (
    <Card className="stat-card">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-2">
          <Icon size={20} className={colorMap[color] || colorMap.blue} />
          {change !== undefined && (
            <span className={`flex items-center text-xs font-medium ${isUp ? 'text-emerald-600' : change < 0 ? 'text-red-500' : 'text-slate-400'}`}>
              {isUp ? <TrendingUp size={12} className="mr-0.5" /> : change < 0 ? <TrendingDown size={12} className="mr-0.5" /> : null}
              {change > 0 ? '+' : ''}{change}%
            </span>
          )}
        </div>
        <p className="text-2xl font-bold">{prefix}{typeof value === 'number' ? fmt(value) : value}</p>
        <p className="text-xs text-slate-500 mt-1">{label}</p>
      </CardContent>
    </Card>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3 text-sm">
      <p className="font-medium text-slate-700 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="text-xs">
          {p.name}: {p.name.toLowerCase().includes('revenue') ? `Rp ${fmt(p.value)}` : fmt(p.value)}
        </p>
      ))}
    </div>
  );
};

export default function TenantAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  useEffect(() => {
    setLoading(true);
    api.get(`/tenant/analytics?days=${days}`).then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [days]);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2D6CDF]" /></div>;
  if (!data) return <p className="text-slate-500 text-center py-12">Gagal memuat data analytics</p>;

  const s = data.summary;

  return (
    <div data-testid="tenant-analytics">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>Analytics</h1>
        <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
          {[7, 14, 30, 90].map(d => (
            <Button key={d} size="sm" variant={days === d ? 'default' : 'ghost'} className={`text-xs h-7 px-3 ${days === d ? '' : 'text-slate-600'}`} onClick={() => setDays(d)}>
              {d}h
            </Button>
          ))}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <StatCard icon={DollarSign} label="Omzet" value={s.revenue} prefix="Rp " change={s.revenue_change} color="green" />
        <StatCard icon={ShoppingCart} label="Pesanan" value={`${s.paid_orders}/${s.orders}`} change={s.orders_change} color="blue" />
        <StatCard icon={Percent} label="Conversion Rate" value={`${s.conversion_rate}%`} change={s.conversion_change} color="amber" />
        <StatCard icon={DollarSign} label="Rata-rata Order" value={s.aov} prefix="Rp " change={s.aov_change} color="purple" />
        <StatCard icon={Users} label="Customer Baru" value={s.new_customers} change={s.customers_change} color="rose" />
      </div>

      {/* Revenue Chart */}
      <Card className="mb-6 border-slate-200 shadow-sm">
        <CardHeader className="pb-2"><CardTitle className="text-base">Grafik Omzet & Pesanan</CardTitle></CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={data.revenue_chart}>
              <defs>
                <linearGradient id="colorRev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10B981" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={v => v.slice(5)} />
              <YAxis yAxisId="rev" tick={{ fontSize: 11 }} tickFormatter={v => v >= 1000000 ? `${v/1000000}jt` : v >= 1000 ? `${v/1000}rb` : v} />
              <YAxis yAxisId="ord" orientation="right" tick={{ fontSize: 11 }} />
              <Tooltip content={<CustomTooltip />} />
              <Area yAxisId="rev" type="monotone" dataKey="revenue" name="Revenue" stroke="#10B981" fill="url(#colorRev)" strokeWidth={2} />
              <Bar yAxisId="ord" dataKey="orders" name="Pesanan" fill="#2D6CDF" opacity={0.3} barSize={12} />
            </AreaChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Top Products */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Package size={16} /> Top Produk</CardTitle></CardHeader>
          <CardContent>
            {data.top_products?.length === 0 ? <p className="text-sm text-slate-500 text-center py-6">Belum ada data</p> : (
              <div className="space-y-3">
                {data.top_products.map((p, i) => {
                  const maxRev = data.top_products[0]?.revenue || 1;
                  return (
                    <div key={i}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="font-medium text-slate-900 truncate max-w-[60%]">{i + 1}. {p.name}</span>
                        <span className="text-slate-500">{p.sold} terjual &middot; Rp {fmt(p.revenue)}</span>
                      </div>
                      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${(p.revenue / maxRev) * 100}%`, backgroundColor: COLORS[i % COLORS.length] }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Hourly Distribution */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="pb-2"><CardTitle className="text-base">Jam Ramai Pesanan</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.hourly_chart}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="hour" tick={{ fontSize: 10 }} interval={2} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="orders" name="Pesanan" fill="#2D6CDF" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Payment Methods */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><CreditCard size={16} /> Metode Bayar</CardTitle></CardHeader>
          <CardContent>
            {data.payment_methods?.length === 0 ? <p className="text-sm text-slate-500 text-center py-6">Belum ada data</p> : (
              <>
                <ResponsiveContainer width="100%" height={160}>
                  <PieChart>
                    <Pie data={data.payment_methods} dataKey="count" nameKey="method" cx="50%" cy="50%" innerRadius={40} outerRadius={65} paddingAngle={2}>
                      {data.payment_methods.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => fmt(v)} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex flex-wrap gap-2 mt-2 justify-center">
                  {data.payment_methods.map((m, i) => (
                    <span key={i} className="flex items-center gap-1 text-xs text-slate-600">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                      {m.method} ({m.count})
                    </span>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* COD Split */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Truck size={16} /> COD vs Transfer</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={160}>
              <PieChart>
                <Pie data={[{ name: 'COD', value: data.cod_split.cod }, { name: 'Transfer', value: data.cod_split.non_cod }]} dataKey="value" cx="50%" cy="50%" innerRadius={40} outerRadius={65} paddingAngle={2}>
                  <Cell fill="#F59E0B" />
                  <Cell fill="#2D6CDF" />
                </Pie>
                <Tooltip formatter={(v) => fmt(v)} />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex justify-center gap-4 mt-2">
              <span className="flex items-center gap-1 text-xs"><span className="w-2.5 h-2.5 rounded-full bg-amber-500" /> COD ({data.cod_split.cod})</span>
              <span className="flex items-center gap-1 text-xs"><span className="w-2.5 h-2.5 rounded-full bg-[#2D6CDF]" /> Transfer ({data.cod_split.non_cod})</span>
            </div>
          </CardContent>
        </Card>

        {/* Promo Usage */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Tag size={16} /> Penggunaan Promo</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-4 py-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2"><Tag size={16} className="text-purple-500" /><span className="text-sm text-slate-700">Voucher</span></div>
                <span className="text-lg font-bold">{data.promo.voucher_used}x</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2"><Star size={16} className="text-amber-500" /><span className="text-sm text-slate-700">Loyalty Points</span></div>
                <span className="text-lg font-bold">{data.promo.loyalty_used}x</span>
              </div>
              <div className="border-t border-slate-100 pt-3">
                <p className="text-xs text-slate-500">Total diskon loyalty</p>
                <p className="text-lg font-bold text-amber-600">Rp {fmt(data.promo.total_loyalty_discount)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Affiliate Performance */}
      <Card className="border-slate-200 shadow-sm mb-6">
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Share2 size={16} /> Performa Affiliate</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="bg-blue-50 rounded-lg p-3">
              <p className="text-xs text-blue-600">Pesanan via Affiliate</p>
              <p className="text-xl font-bold text-blue-700">{data.affiliate.total_orders}</p>
            </div>
            <div className="bg-emerald-50 rounded-lg p-3">
              <p className="text-xs text-emerald-600">Revenue via Affiliate</p>
              <p className="text-xl font-bold text-emerald-700">Rp {fmt(data.affiliate.total_revenue)}</p>
            </div>
          </div>
          {data.affiliate.top_affiliates?.length > 0 ? (
            <table className="w-full text-sm">
              <thead><tr className="border-b border-slate-100">
                <th className="text-left py-2 px-2 text-slate-500 font-medium">#</th>
                <th className="text-left py-2 px-2 text-slate-500 font-medium">Affiliate</th>
                <th className="text-right py-2 px-2 text-slate-500 font-medium">Pesanan</th>
                <th className="text-right py-2 px-2 text-slate-500 font-medium">Revenue</th>
              </tr></thead>
              <tbody>
                {data.affiliate.top_affiliates.map((a, i) => (
                  <tr key={i} className="border-b border-slate-50">
                    <td className="py-2 px-2 text-slate-400">{i + 1}</td>
                    <td className="py-2 px-2 font-medium">{a.name} <span className="text-xs text-slate-400">({a.code})</span></td>
                    <td className="py-2 px-2 text-right">{a.orders}</td>
                    <td className="py-2 px-2 text-right font-medium text-emerald-600">Rp {fmt(a.revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <p className="text-sm text-slate-500 text-center py-4">Belum ada transaksi via affiliate</p>}
        </CardContent>
      </Card>

      {/* Courier Breakdown */}
      {data.couriers?.length > 0 && (
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Truck size={16} /> Ekspedisi</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={data.couriers} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="courier" tick={{ fontSize: 11 }} width={80} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="count" name="Pesanan" fill="#8B5CF6" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
